import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllVotes,
  getUserBallots,
} from "../../../../../services/voteServices";

import {
  DEFAULT_VOTE_FILTERS,
  filterVotes,
  getBallotForVote,
  getStoredUserId,
  getVoteStatus,
  sortVotesForUser,
} from "../utils/userVoteUtils";

export function useUserVoteDashboard() {
  const [votes, setVotes] = useState([]);
  const [ballots, setBallots] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState(DEFAULT_VOTE_FILTERS);

  const [selectedVoteId, setSelectedVoteId] = useState(null);
  const [participateOpen, setParticipateOpen] = useState(false);
  const [answersOpen, setAnswersOpen] = useState(false);

  const userId = useMemo(() => getStoredUserId(), []);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);

      const [votesData, ballotsData] = await Promise.all([
        getAllVotes(),
        userId ? getUserBallots(userId) : Promise.resolve([]),
      ]);

      const safeBallots = Array.isArray(ballotsData) ? ballotsData : [];

      const publicVotes = (Array.isArray(votesData) ? votesData : []).filter(
        (vote) => {
          const hasBallot = getBallotForVote(safeBallots, vote?.id);
          return vote?.is_active || hasBallot;
        }
      );

      setVotes(sortVotesForUser(publicVotes, safeBallots));
      setBallots(safeBallots);
    } catch (error) {
      console.error("Erreur chargement votes utilisateur :", error);
      toast.error("Erreur lors du chargement des votes et sondages.");
      setVotes([]);
      setBallots([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const getBallot = useCallback(
    (voteId) => getBallotForVote(ballots, voteId),
    [ballots]
  );

  const filteredVotes = useMemo(() => {
    return filterVotes(votes, ballots, filters);
  }, [votes, ballots, filters]);

  const stats = useMemo(() => {
    const total = votes.length;

    const answered = votes.filter((vote) => getBallot(vote.id)).length;

    const available = votes.filter((vote) => {
      const status = getVoteStatus(vote);
      return status.key === "available" && !getBallot(vote.id);
    }).length;

    const upcoming = votes.filter((vote) => getVoteStatus(vote).key === "upcoming").length;

    const closed = votes.filter((vote) => getVoteStatus(vote).key === "closed").length;

    const sondages = votes.filter(
      (vote) => String(vote?.type || "").toLowerCase() === "sondage"
    ).length;

    return {
      total,
      answered,
      available,
      upcoming,
      closed,
      sondages,
    };
  }, [votes, getBallot]);

  const openParticipate = useCallback((voteId) => {
    setSelectedVoteId(voteId);
    setParticipateOpen(true);
    setAnswersOpen(false);
  }, []);

  const closeParticipate = useCallback(() => {
    setParticipateOpen(false);
    setSelectedVoteId(null);
  }, []);

  const openAnswers = useCallback((voteId) => {
    setSelectedVoteId(voteId);
    setAnswersOpen(true);
    setParticipateOpen(false);
  }, []);

  const closeAnswers = useCallback(() => {
    setAnswersOpen(false);
    setSelectedVoteId(null);
  }, []);

  return {
    userId,

    votes,
    filteredVotes,
    ballots,
    loading,

    filters,
    setFilters,
    stats,

    selectedVoteId,
    participateOpen,
    answersOpen,

    refresh,
    openParticipate,
    closeParticipate,
    openAnswers,
    closeAnswers,
    getBallot,
  };
}

export default useUserVoteDashboard;